import React, { useEffect, useRef, useState } from "react";
import { MDialog, MSpinner } from "components/Shared";
import { Button, DatePicker } from "react-md";
import { GenerateErrorReportRequest } from "interfaces/Mueller.Business.Models";
import { generateErrorReport } from "requests/phoenixForms";
import moment from "moment";

interface Props {
  formName: string;
  formId: string;
  onDismiss: () => void;
}

enum DialogState { ENTERING, SUBMITTING, SUCCESS, ERROR }

const GenerateErrorReportDialog = ({
  formName,
  formId,
  onDismiss
} : Props) => {

  const mounted = useRef<boolean>(true);
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);
  const [errorText, setErrorText] = useState<string>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.ENTERING);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => setErrorText(null), [startDate, endDate]);

  const onSubmit = () => {
    if (!startDate || !endDate) {
      setErrorText("Must select a start and end date");
      return;
    }
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const diffDays = endMoment.diff(startMoment, "days");
    if (diffDays > 7) {
      setErrorText("Date range can be a maximum of 7 days");
      return;
    }

    const request: GenerateErrorReportRequest = {
      phoenixFormId: formId,
      startDate: startDate,
      endDate: endDate,
    };
    
    setDialogState(DialogState.SUBMITTING);
    generateErrorReport(
      request,
      () => {
        setDialogState(DialogState.SUCCESS);
      },
      () => {
        setDialogState(DialogState.ERROR);
      },
      mounted
    );
  };

  const buttons = <>
    {dialogState === DialogState.ENTERING &&
    <>
      <Button
        raised
        onClick={onDismiss}
      >Cancel</Button>
      <Button
        style={{marginLeft: 10}}
        primary
        raised
        onClick={onSubmit}
      >Submit</Button>
    </>
    }
    {(dialogState === DialogState.SUCCESS || dialogState === DialogState.ERROR) &&
      <Button
        primary
        raised
        onClick={onDismiss}
      >OK</Button>
    }
  </>;

  return (
    <MDialog
      id="generate-error-report-dialog"
      title="Generate Error Report"
      visible
      dontSpecifyWidth
      contentActions={buttons}
    >
      {dialogState === DialogState.ENTERING &&
        <div>
          <h2>{formName}</h2>
          <div>
            <DatePicker
              id="startDate"
              onChange={(_, date) => setStartDate(date)}
              label="Start Date:"
              value={startDate}
              autoOk
              portal
              renderNode={document.getElementById("modal-root")} // this is so datepicker doesn't render under the dialog, consequence of react-md being 5 major versions behind
            />
          </div>
          <div>
            <DatePicker
              id="endDate"
              onChange={(_, date) => setEndDate(date)}
              label="End Date:"
              value={endDate}
              autoOk
              portal
              renderNode={document.getElementById("modal-root")}
            />
          </div>
          <p className="validation-error">{errorText}</p>
        </div>
      }
      {dialogState === DialogState.SUBMITTING &&
        <MSpinner />
      }
      {dialogState === DialogState.SUCCESS &&
        <div>
          <p>The error report was queued for generation.</p>
          <p>You will receive an email once the process is completed.</p>
        </div>
      }
      {dialogState === DialogState.ERROR &&
        <p>Error generating error report. Oh the irony.</p>
      }
    </MDialog>
  );
};

export default GenerateErrorReportDialog;
